import { type ReactNode, useState } from 'react'
import { type TFunction } from 'i18next'
import { useTranslation } from 'react-i18next'
import { DetailsList } from '@/components/DetailsList/DetailsList'
import { BUNDLE_DISCOUNT_PERCENTAGE } from '@/features/bundleDiscount/bundleDiscount.constants'
import {
  isOfferRecommendation,
  type OfferRecommendation,
  type ProductRecommendation,
} from '@/features/CrossSell/hooks/useRecommendations'
import { convertToDate } from '@/utils/date'
import { type Formatter } from '@/utils/formatter'
import { useFormatter } from '@/utils/useFormatter'
import { Button } from 'ui/src/components/Button/Button'
import { Card } from 'ui/src/components/Card/Card'
import { Collapsible } from 'ui/src/components/Collapsible/Collapsible'
import { Text } from 'ui/src/components/Text/Text'
import { SwapIcon } from 'ui/src/icons/SwapIcon'
import { xStack, yStack } from 'ui/src/patterns/stack'
import { ExternalInsurerLogo } from '../ExternalInsurerLogo/ExternalInsurerLogo'
import { getPriceMatchMonthlySavingsWithBundleDiscount } from './getPriceMatchMonthlySavingsWithBundleDiscount'
import { ribbon } from './RecommendedOffer.css'

type Props = {
  recommendation: OfferRecommendation | ProductRecommendation
  isExpanded?: boolean
  defaultExpanded?: boolean
  onExpandedChange?: (isExpanded: boolean) => void
  children: ReactNode
}

export function RecommendationCard({
  recommendation,
  isExpanded: externalIsExpanded,
  defaultExpanded = true,
  onExpandedChange,
  children,
}: Props) {
  const { t } = useTranslation(['cart', 'purchase-form', 'common'])
  const formatter = useFormatter()
  const [innerIsExpanded, setInnerIsExpanded] = useState(defaultExpanded)

  const { product, externalInsurance } = recommendation
  const offer = isOfferRecommendation(recommendation) ? recommendation.offer : null
  const priceMatch = offer?.priceMatch ?? null
  const isCarOffer = product.name === 'SE_CAR'

  const hasOfferPriceMatch = offer != null && priceMatch != null

  if (!hasOfferPriceMatch && !externalInsurance) return null

  const renewalDateSource = hasOfferPriceMatch ? priceMatch.renewalDate : externalInsurance?.renewalDate
  const renewalDate = renewalDateSource ? convertToDate(renewalDateSource) : null

  const addressOrRegistrationItem = offer?.displayItems.find((item) =>
    isCarOffer ? item.key === 'registrationNumber' : item.key === 'street',
  )

  const isExpanded = externalIsExpanded ?? innerIsExpanded

  const handleExpandedChange = (newIsExpanded: boolean) => {
    if (externalIsExpanded == null) setInnerIsExpanded(newIsExpanded)
    onExpandedChange?.(newIsExpanded)
  }

  const insurerLogoProps = hasOfferPriceMatch
    ? {
        insurelyId: priceMatch.externalInsurer.insurelyId,
        displayName: priceMatch.externalInsurer.displayName,
      }
    : {
        insurelyId: externalInsurance!.insurer.insurelyId,
        displayName: externalInsurance!.insurer.displayName,
      }

  const ribbonText = getRibbonText({
    offer,
    priceMatch,
    formatter,
    t,
  })

  return (
    <Card.Root className={yStack({ gap: 'lg' })}>
      {ribbonText && (
        <Card.Ribbon className={ribbon}>
          <Text as="p" size="sm" color="signalGreenText" align="center" singleLine>
            {ribbonText}
          </Text>
        </Card.Ribbon>
      )}

      <Collapsible.Root open={isExpanded} onOpenChange={handleExpandedChange}>
        <div className={yStack({ gap: 'sm' })}>
          <Card.Header>
            <div className={xStack({ gap: 'sm', alignItems: 'flex-start', width: '100%' })}>
              <Card.Media>
                <ExternalInsurerLogo
                  insurelyId={insurerLogoProps.insurelyId}
                  displayName={insurerLogoProps.displayName}
                />
              </Card.Media>
              <Card.Heading>
                {hasOfferPriceMatch ? (
                  <>
                    <Card.Title variant={{ _: 'standard.16', sm: 'standard.18' }}>
                      {priceMatch.externalInsuranceName}
                    </Card.Title>
                    <Card.Subtitle size={{ _: 'sm', sm: 'md' }}>
                      {formatter.monthlyPrice({
                        currencyCode: priceMatch.externalPrice.currencyCode,
                        amount: priceMatch.externalPrice.amount,
                      })}
                    </Card.Subtitle>
                  </>
                ) : (
                  <>
                    <Card.Title>{externalInsurance!.displayName}</Card.Title>
                    {externalInsurance?.price != null ? (
                      <Card.Subtitle size="sm">
                        {formatter.monthlyPrice({
                          currencyCode: externalInsurance.price.currencyCode,
                          amount: externalInsurance.price.amount,
                        })}
                      </Card.Subtitle>
                    ) : null}
                  </>
                )}
              </Card.Heading>
            </div>
          </Card.Header>

          {!isExpanded && (
            <Collapsible.Trigger asChild>
              <Button variant="secondary" size="medium" fullWidth>
                {t('cart:RECOMMENDED_OFFER_SEE_OFFER_BUTTON')}
              </Button>
            </Collapsible.Trigger>
          )}

          <Collapsible.Content>
            <div className={yStack({ gap: 'lg' })}>
              <DetailsList.Root>
                {hasOfferPriceMatch ? (
                  <>
                    {addressOrRegistrationItem && (
                      <DetailsList.Item>
                        <DetailsList.Label>
                          {isCarOffer ? t('DATA_TABLE_REGISTRATION_NUMBER_LABEL') : t('DATA_TABLE_STREET_LABEL')}
                        </DetailsList.Label>
                        <DetailsList.Value>{addressOrRegistrationItem.displayValue}</DetailsList.Value>
                      </DetailsList.Item>
                    )}
                    {renewalDate && (
                      <DetailsList.Item>
                        <DetailsList.Label>{t('cart:RECOMMENDED_OFFER_RENEWAL_DATE_LABEL')}</DetailsList.Label>
                        <DetailsList.Value>{formatter.fromNow(renewalDate)}</DetailsList.Value>
                      </DetailsList.Item>
                    )}
                  </>
                ) : (
                  <>
                    {externalInsurance?.insuredObject && (
                      <DetailsList.Item>
                        <DetailsList.Label>{getSubtitleLabel(product.name)}</DetailsList.Label>
                        <DetailsList.Value>{externalInsurance.insuredObject}</DetailsList.Value>
                      </DetailsList.Item>
                    )}
                    {renewalDate && (
                      <DetailsList.Item>
                        <DetailsList.Label>{t('cart:RECOMMENDED_OFFER_RENEWAL_DATE_LABEL')}</DetailsList.Label>
                        <DetailsList.Value>{formatter.fromNow(renewalDate)}</DetailsList.Value>
                      </DetailsList.Item>
                    )}
                  </>
                )}
              </DetailsList.Root>

              <Card.Divider icon={<SwapIcon size="24px" aria-hidden="true" />} />

              {children}
            </div>
          </Collapsible.Content>
        </div>
      </Collapsible.Root>
    </Card.Root>
  )
}

function getSubtitleLabel(productName: string): string {
  if (productName === 'SE_CAR') return 'Registreringsnummer'
  if (productName.startsWith('SE_APARTMENT') || productName === 'SE_HOUSE') return 'Adress'
  return 'Namn'
}

type GetRibbonTextParams = {
  offer: OfferRecommendation['offer'] | null
  priceMatch: OfferRecommendation['offer']['priceMatch'] | null
  formatter: Formatter
  t: TFunction<readonly ['cart', 'purchase-form', 'common']>
}

function getRibbonText({ offer, priceMatch, formatter, t }: GetRibbonTextParams): string | null {
  if (offer && priceMatch) {
    const monthlySavingsWithBundle = getPriceMatchMonthlySavingsWithBundleDiscount(offer, priceMatch)

    if (monthlySavingsWithBundle.amount > 0) {
      return t('cart:RECOMMENDED_OFFER_SAVINGS_BADGE', {
        amount: formatter.annualReduction(monthlySavingsWithBundle),
      })
    }
  }

  return t('cart:RECOMMENDED_OFFER_GET_BUNDLE_DISCOUNT', { percentage: BUNDLE_DISCOUNT_PERCENTAGE })
}
